import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const ToastContext = createContext(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const counterRef = useRef(0);

  const addToast = useCallback((message, type = 'info') => {
    const id = ++counterRef.current;
    setToasts(prev => {
      const next = [...prev, { id, message, type }];
      // Max 4 toasts — remove oldest if exceeded
      return next?.length > 4 ? next?.slice(next?.length - 4) : next;
    });
    setTimeout(() => {
      setToasts(prev => prev?.filter(t => t?.id !== id));
    }, 4000);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev?.filter(t => t?.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

const TYPE_CONFIG = {
  success: {
    bg: 'bg-emerald-50 border-emerald-200',
    icon: '✓',
    iconClass: 'bg-emerald-500 text-white',
    textClass: 'text-emerald-800',
  },
  error: {
    bg: 'bg-red-50 border-red-200',
    icon: '✕',
    iconClass: 'bg-red-500 text-white',
    textClass: 'text-red-800',
  },
  warning: {
    bg: 'bg-amber-50 border-amber-200',
    icon: '⚠',
    iconClass: 'bg-amber-500 text-white',
    textClass: 'text-amber-800',
  },
  info: {
    bg: 'bg-blue-50 border-blue-200',
    icon: 'i',
    iconClass: 'bg-blue-500 text-white',
    textClass: 'text-blue-800',
  },
};

function ToastContainer({ toasts, onRemove }) {
  if (toasts?.length === 0) return null;
  return (
    <div
      className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none"
      style={{ maxWidth: '360px', width: 'calc(100vw - 2rem)' }}
    >
      {toasts?.map(toast => (
        <ToastItem key={toast?.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onRemove }) {
  const cfg = TYPE_CONFIG?.[toast?.type] || TYPE_CONFIG?.info;
  return (
    <div
      className={`pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-xl border shadow-lg ${cfg?.bg}`}
      style={{
        fontFamily: '"IBM Plex Sans", sans-serif',
        animation: 'toastSlideIn 0.25s ease-out',
      }}
      role="alert"
    >
      <style>{`
        @keyframes toastSlideIn {
          from { opacity: 0; transform: translateX(24px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
      <span
        className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${cfg?.iconClass}`}
        aria-hidden="true"
      >
        {cfg?.icon}
      </span>
      <p className={`flex-1 text-sm font-medium leading-snug ${cfg?.textClass}`}>
        {toast?.message}
      </p>
      <button
        onClick={() => onRemove(toast?.id)}
        className={`flex-shrink-0 ml-1 text-lg leading-none opacity-50 hover:opacity-100 transition-opacity ${cfg?.textClass}`}
        aria-label="Cerrar notificación"
      >
        ×
      </button>
    </div>
  );
}
